import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Eye, Heart, MessageCircle, Search, Loader2, Sparkles, Lock } from 'lucide-react';

// Optional inspiration prompts. Slugs are the ONLY thing passed through the
// query string; the label + emoji stays here (client-only, not a DB category).
const INSPIRATION_PROMPTS = [
  { slug: 'fantasia',  emoji: '🔥', label: 'Uma fantasia que nunca contei' },
  { slug: 'segredo',   emoji: '😈', label: 'Algo que fiz e ninguém sabe' },
  { slug: 'desejo',    emoji: '💭', label: 'Algo que desejo experimentar' },
  { slug: 'pessoa',    emoji: '❤️', label: 'Uma pessoa que não consigo esquecer' },
  { slug: 'vergonha',  emoji: '🙈', label: 'Uma situação que me deixou com vergonha' },
  { slug: 'livre',     emoji: '✍️', label: 'Quero contar à minha maneira' },
];

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function ConfessionCard({ confession, onClick }) {
  const preview = confession.excerpt || stripHtml(confession.content);
  return (
    <Card 
      className="relative bg-surface border-border cursor-pointer group transition-all hover:border-ruby/50 hover:shadow-lg hover:shadow-ruby/10 overflow-hidden"
      onClick={onClick}
      data-testid={'confession-card-' + confession.id}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-semibold text-foreground group-hover:text-ruby transition-colors line-clamp-2">
            {confession.title}
          </h3>
          {confession.category && (
            <Badge variant="outline" className="border-ruby/50 text-ruby text-xs shrink-0">
              {confession.category}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {preview}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="italic">
            {confession.author_name || 'Anónimo'}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {confession.views || 0}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" />
              {confession.likes || 0}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Confessions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confessions, setConfessions] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const goToConfess = (slug) => {
    // "livre" (write freely) intentionally opens the clean form.
    if (!slug || slug === 'livre') return navigate('/confess');
    navigate(`/confess?prompt=${slug}`);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      const [confRes, featuredRes, catRes] = await Promise.all([
        api.get('/confessions' + (params.toString() ? '?' + params.toString() : '')),
        api.get('/confessions/featured?limit=4'),
        api.get('/confessions/categories').catch(() => ({ data: [] })),
      ]);
      setConfessions(confRes.data);
      setFeatured(featuredRes.data);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredConfessions = searchTerm
    ? confessions.filter(c => 
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.content.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : confessions;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero — emotional invite */}
        <div className="relative py-16 px-6 mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-ruby/20 via-transparent to-ruby/10" />
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-ruby/20 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-ruby" />
              <span className="text-ruby font-medium">Confissões</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-5 leading-tight" data-testid="confessions-hero-title">
              Há coisas que nunca dirias em voz alta.
              <span className="block text-ruby mt-1">Aqui podes dizê-las.</span>
            </h1>

            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Um desejo secreto. Uma fantasia. Algo que fizeste. Algo que gostarias de fazer.
              Ou aquela história que nunca contaste a ninguém.
            </p>

            <div className="flex flex-col items-center gap-3 mb-4">
              <Button
                onClick={() => goToConfess()}
                className="bg-ruby hover:bg-ruby-light text-base px-8 py-6"
                data-testid="submit-confession-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Fazer uma confissão
              </Button>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="confession-privacy-reassurance">
                <Lock className="w-3.5 h-3.5 text-ruby" />
                A tua identidade não será mostrada aos outros leitores.
              </p>
              {!user && (
                <p className="text-xs text-muted-foreground/80" data-testid="confession-login-hint">
                  É necessário iniciar sessão para enviar uma confissão.
                </p>
              )}
              <p className="text-[11px] text-muted-foreground/60 max-w-md leading-relaxed">
                A equipa Intimate mantém um registo interno do autor por razões de segurança e moderação.
              </p>
            </div>

            {/* Inspiration prompts — writing unblockers, NOT categories */}
            <div className="mt-6" data-testid="confession-prompts">
              <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-3">
                Precisas de uma pista?
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {INSPIRATION_PROMPTS.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => goToConfess(p.slug)}
                    className="text-sm bg-surface hover:bg-ruby/10 border border-border hover:border-ruby/50 text-foreground/90 hover:text-foreground px-3.5 py-1.5 rounded-full transition-colors"
                    data-testid={`confession-prompt-${p.slug}`}
                  >
                    <span className="mr-1.5">{p.emoji}</span>{p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search — kept below the conversion area */}
            <div className="relative max-w-md mx-auto mt-10">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar confissões..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-surface border-border"
                data-testid="confession-search"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              onClick={() => setSelectedCategory('')}
              className={!selectedCategory ? "bg-ruby hover:bg-ruby-light" : "border-border"}
              size="sm"
            >
              Todas
            </Button>
            {categories.map((cat) => {
              const name = typeof cat === 'string' ? cat : cat.name;
              const id = typeof cat === 'string' ? cat : (cat.id || cat.name);
              return (
                <Button
                  key={id}
                  variant={selectedCategory === name ? "default" : "outline"}
                  onClick={() => setSelectedCategory(selectedCategory === name ? '' : name)}
                  className={selectedCategory === name ? "bg-ruby hover:bg-ruby-light" : "border-border"}
                  size="sm"
                  data-testid={`confession-category-${id}`}
                >
                  {name}
                </Button>
              );
            })}
          </div>

          {/* Featured */}
          {!selectedCategory && !searchTerm && featured.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Heart className="w-6 h-6 text-ruby" />
                Mais Populares
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {featured.map((conf) => (
                  <ConfessionCard 
                    key={conf.id} 
                    confession={conf} 
                    onClick={() => navigate('/confessions/' + (conf.slug || conf.id))}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Confessions */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-ruby" />
              {selectedCategory || 'Todas as Confissões'}
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-ruby" />
              </div>
            ) : filteredConfessions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredConfessions.map((conf, idx) => (
                  <React.Fragment key={conf.id}>
                    <ConfessionCard
                      confession={conf}
                      onClick={() => navigate('/confessions/' + (conf.slug || conf.id))}
                    />
                    {/* Conversion card intercalated every 6 items — same design system, subtle */}
                    {(idx + 1) % 6 === 0 && idx !== filteredConfessions.length - 1 && (
                      <Card
                        className="bg-ruby/10 border-ruby/40 hover:border-ruby transition-colors cursor-pointer"
                        onClick={() => goToConfess()}
                        data-testid={`confession-feed-cta-${idx}`}
                      >
                        <CardContent className="p-5 h-full flex flex-col justify-center text-center">
                          <p className="text-foreground font-medium leading-relaxed mb-1">
                            Estás a ler os segredos dos outros… 👀
                          </p>
                          <p className="text-sm text-muted-foreground mb-4">
                            Está na altura de deixares o teu.
                          </p>
                          <div>
                            <Button
                              size="sm"
                              className="bg-ruby hover:bg-ruby-light"
                              onClick={(e) => { e.stopPropagation(); goToConfess(); }}
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Fazer uma confissão
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <MessageCircle className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Nenhuma confissão encontrada
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm ? 'Tenta outros termos de pesquisa' : 'Seja o primeiro a partilhar!'}
                </p>
                <Button
                  onClick={() => goToConfess()}
                  className="bg-ruby hover:bg-ruby-light"
                >
                  Partilhar a Minha Confissão
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
